
import React, { useState, useMemo } from 'react';
import { GAMES } from './constants';
import { GameId } from './types';
import GameCard from './components/GameCard';

// Direct Game Imports
import Snake from './games/Snake';
import Trivia from './games/Trivia';
import Hangman from './games/Hangman';
import Memory from './games/Memory';
import Clicker from './games/Clicker';
import Minesweeper from './games/Minesweeper';
import Binary2048 from './games/Binary2048';
import TicTacToe from './games/TicTacToe';
import Pong from './games/Pong';
import Wordle from './games/Wordle';
import Breakout from './games/Breakout';
import Flappy from './games/Flappy';
import Simon from './games/Simon';
import RPS from './games/RPS';
import Sudoku from './games/Sudoku';
import Flood from './games/Flood';
import Typing from './games/Typing';
import Connect4 from './games/Connect4';
import Invaders from './games/Invaders';
import Maze from './games/Maze';
import Whack from './games/Whack';
import Reaction from './games/Reaction';
import Tower from './games/Tower';
import Jump from './games/Jump';
import Hex from './games/Hex';

const App: React.FC = () => {
  const [activeGame, setActiveGame] = useState<GameId | null>(null);

  const selectedGameMetadata = useMemo(() => 
    GAMES.find(g => g.id === activeGame), 
  [activeGame]);

  const renderGame = () => {
    switch (activeGame) {
      case 'snake': return <Snake />;
      case 'trivia': return <Trivia />;
      case 'hangman': return <Hangman />;
      case 'memory': return <Memory />;
      case 'clicker': return <Clicker />;
      case 'minesweeper': return <Minesweeper />;
      case '2048': return <Binary2048 />;
      case 'tictactoe': return <TicTacToe />;
      case 'pong': return <Pong />;
      case 'wordle': return <Wordle />;
      case 'breakout': return <Breakout />;
      case 'flappy': return <Flappy />;
      case 'simon': return <Simon />;
      case 'rps': return <RPS />;
      case 'sudoku': return <Sudoku />;
      case 'flood': return <Flood />;
      case 'typing': return <Typing />;
      case 'connect4': return <Connect4 />;
      case 'invaders': return <Invaders />;
      case 'maze': return <Maze />;
      case 'whack': return <Whack />;
      case 'reaction': return <Reaction />;
      case 'tower': return <Tower />;
      case 'jump': return <Jump />;
      case 'hex': return <Hex />;
      default: return <div className="text-white">Game Not Found</div>;
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col w-full">
      {/* Navigation Header */}
      <header className="sticky top-0 w-full glass px-6 py-4 z-[1000] border-b border-white/10">
        <div className="max-w-7xl mx-auto flex flex-row justify-between items-center w-full">
          <div 
            className="flex flex-row items-center gap-3 cursor-pointer select-none"
            onClick={() => setActiveGame(null)}
          >
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/30 shrink-0">
              <span className="text-xl">🕹️</span>
            </div>
            <div className="flex flex-col">
              <h1 className="text-xl font-black text-white leading-none tracking-tighter uppercase">NIFTYARCADE</h1>
              <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">HD Playroom</span>
            </div>
          </div>

          <div className="flex flex-row items-center gap-4">
            {activeGame && (
              <button 
                onClick={() => setActiveGame(null)}
                className="bg-red-600 hover:bg-red-700 text-white font-black text-[10px] px-4 py-2 rounded-full uppercase tracking-widest transition-all shadow-lg active:scale-95 whitespace-nowrap"
              >
                Exit Game
              </button>
            )}
            <a 
              href="https://niftytechfinds.com" 
              className="hidden sm:block text-[10px] font-bold text-slate-400 hover:text-white transition-colors uppercase tracking-widest whitespace-nowrap"
            >
              niftytechfinds.com
            </a>
          </div>
        </div>
      </header>

      {/* Main Viewport */}
      <main className="flex-1 w-full max-w-7xl mx-auto px-6 py-8 flex flex-col">
        {!activeGame ? (
          <div className="w-full">
            <div className="mb-12">
              <h2 className="text-5xl md:text-7xl font-black text-white tracking-tighter leading-[1] mb-6">
                25 Infinite <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-600">
                  Arcade Modules.
                </span>
              </h2>
              <p className="text-slate-400 text-lg md:text-xl max-w-2xl font-medium">
                High-performance classic games reimagined for tech enthusiasts. Zero latency, optimized for 4K.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full">
              {GAMES.map((game) => (
                <GameCard 
                  key={game.id} 
                  game={game} 
                  onClick={() => {
                    setActiveGame(game.id as GameId);
                    window.scrollTo(0, 0);
                  }} 
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="w-full flex flex-col items-center">
            <div className="text-center mb-10">
              <span className="text-[10px] font-black text-blue-500 uppercase tracking-[0.4em] mb-2 block">System Module Loaded</span>
              <h2 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tighter leading-none">
                {selectedGameMetadata?.title}
              </h2>
            </div>
            
            <div className="w-full max-w-4xl flex flex-col items-center justify-center bg-slate-900/60 p-4 sm:p-10 rounded-[2rem] border border-white/10 shadow-2xl min-h-[500px]">
              {renderGame()}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 px-6 bg-slate-950 mt-auto">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4 text-slate-500">
          <p className="text-[10px] font-bold uppercase tracking-widest">© 2024 Nifty Arcade - Premium Edition</p>
          <div className="flex flex-row items-center gap-4">
             <div className="flex flex-row items-center gap-2">
               <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
               <span className="text-[10px] font-bold uppercase tracking-widest">Engine Stable</span>
             </div>
             <div className="w-px h-4 bg-slate-800"></div>
             <span className="text-[10px] font-bold uppercase tracking-widest">v2.7_RESILIENT</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
